import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  QrCode2 as QrCodeIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  ExitToApp as LogoutIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import qrcodeService, { QRCodeStatistics } from '../services/qrcodeService';
import api from '../services/api';

const Dashboard: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<QRCodeStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    fetchStatistics();
    if (isAdmin) {
      fetchPendingCount();
    }
  }, [isAdmin]);

  const fetchStatistics = async () => {
    try {
      const data = await qrcodeService.getStatistics();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const response = await api.get('/auth/admin/pending-users');
      if (response.data.success) {
        console.log('Pending count:', response.data.data.total);
        setPendingCount(response.data.data.total);
      }
    } catch (error: any) {
      console.error('Failed to fetch pending count:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 56,
                    height: 56,
                  }}
                >
                  <DashboardIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                    Tableau de bord
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bienvenue, {user?.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {isAdmin && (
                  <>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      startIcon={<AdminIcon />}
                      onClick={() => navigate('/admin')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Administration
                    </Button>
                    {pendingCount > 0 && (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => navigate('/admin/pending-users')}
                        sx={{
                          fontWeight: 'bold',
                          animation: 'pulse 2s infinite'
                        }}
                      >
                        {pendingCount} {pendingCount === 1 ? 'compte en attente' : 'comptes en attente'}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="outlined"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  size="small"
                >
                  Déconnexion
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Stats Cards */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <QrCodeIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats?.total_qrcodes || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Codes QR
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <AnalyticsIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats?.total_scans || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scans totaux
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats?.avg_scans_per_qrcode || '0.00'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scans moyens
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4">{stats?.max_scans || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scans maximum
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Quick Actions */}
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Actions rapides
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Créez et gérez vos codes QR avec des options de personnalisation complètes
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/qrcodes/create')}
            >
              Créer un nouveau QR Code
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/qrcodes')}
            >
              Voir tous les codes QR
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;
